import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import EditParticipantDialog from '../../EditParticipantDialog';
import Tag from '../../../Tag';
import RemoveParticipantButton from '../RemoveParticipantButton';
import styles from '../../surveyParticipantsEditor.module.css';
import { participantType, participantIdTypeValues } from '../../../../constants/types';
import { participantErrors, participantWarnings } from '../../../../constants';
import { withLang } from '../../../../i18n';

const getRowStyleAndEmailColumn = (error, warning, email, t) => {
  if (error) {
    const errorText = t(`surveyParticipantsEditor.${error}`);
    const emailErrors = [participantErrors.DUPLICATE_EMAIL, participantErrors.INVALID_EMAIL];
    if (emailErrors.includes(error)) {
      return { rowStyle: styles.notFound, emailColumn: `${errorText}: ${email}` };
    }
    return { rowStyle: styles.notFound, emailColumn: errorText };
  }

  if (warning) {
    return { rowStyle: styles.warning, emailColumn: t(`surveyParticipantsEditor.${warning}`) };
  }

  return { rowStyle: '', emailColumn: email };
};

const StudentRow = ({
  participant,
  name,
  editParticipant,
  reservedEmails,
  translate: t
}) => {
  const {
    error, warning, email, externalId, studentNumber, existingEmail, new: isNew, responseDate
  } = participant;
  // Memoization might be worth here in case we have 1000's of rows
  const { rowStyle, emailColumn } = useMemo(
    () => getRowStyleAndEmailColumn(error, warning, email, t),
    [error, warning, email, t]
  );

  const showEditButton = editParticipant
    && error !== participantErrors.NOT_FOUND
    && warning !== participantWarnings.ALREADY_IN_SURVEY;
  const showRemoveButton = warning !== participantWarnings.ALREADY_IN_SURVEY && !responseDate;

  return (
    <tr className={`${rowStyle} ${styles.item}`}>
      <td className={styles.nameColumn}>{name}</td>
      <td>{emailColumn}</td>
      <td className={styles.idColumn}>
        {studentNumber} {isNew && <Tag text={t('common.new')} inverse />}
      </td>
      <td className={styles.actionsColumn}>
        {error && <span className={`${styles.alert} icon--alert`} />}
        {showEditButton && (
          <EditParticipantDialog
            participant={{
              externalId, email, name, studentNumber
            }}
            editParticipant={editParticipant}
            existingEmail={existingEmail}
            reservedEmails={reservedEmails}
            warning={warning}
          />
        )}
        {showRemoveButton && (
          <RemoveParticipantButton
            participantId={externalId || studentNumber}
            participantIdType={externalId ? participantIdTypeValues.EXTERNAL_ID : participantIdTypeValues.STUDENT_NUMBER}
            translate={t}
          />
        )}
      </td>
    </tr>
  );
};

StudentRow.propTypes = {
  participant: participantType.isRequired,
  name: PropTypes.string,
  editParticipant: PropTypes.func.isRequired,
  reservedEmails: PropTypes.arrayOf(PropTypes.string).isRequired,
  translate: PropTypes.func.isRequired
};

StudentRow.defaultProps = {
  name: ''
};

export default memo(withLang(StudentRow));
